import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from 'urql';
import { useUi } from '@/stores/ui.store';
import { useWorkspace } from '@/stores/workspace.store';
import { NotificationBell } from '@/components/NotificationBell';
import { CommandPalette } from '@/components/CommandPalette';
import { ProjectSwitcher } from '@/components/ProjectSwitcher';
import { ProfileMenu } from '@/components/ProfileMenu';
import { Q_SHELL_COUNTS } from '@/lib/queries';

/** Sidebar items — each gets an optional `badgeKey` mapping into shellCounts. */
const navSections = [
  { label: 'Workspace', items: [
    { id: 'dashboard', label: 'Dashboard', icon: '◫' },
    { id: 'inbox',     label: 'Inbox',     icon: '✉', badgeKey: 'unreadInbox' },
    { id: 'analytics', label: 'Analytics', icon: '◔' },
  ]},
  { label: 'Content', items: [
    { id: 'content',   label: 'Content library', icon: '◰' },
    { id: 'review',    label: 'Review queue',    icon: '◧' },
    { id: 'sources',   label: 'Sources',         icon: '⟲' },
    { id: 'templates', label: 'Templates',       icon: '⬚' },
    { id: 'media',     label: 'Media library',   icon: '◉' },
    { id: 'brand',     label: 'Brand kit',       icon: '◬' },
  ]},
  { label: 'Publishing', items: [
    { id: 'create',     label: 'Create posts', icon: '✦' },
    { id: 'drafts',     label: 'Drafts',       icon: '◳', badgeKey: 'pendingDrafts' },
    { id: 'calendar',   label: 'Calendar',     icon: '▦', badgeKey: 'scheduledPosts' },
    { id: 'automation', label: 'Automation',   icon: '⚡' },
  ]},
  { label: 'Setup', items: [
    { id: 'connections',  label: 'Connections',  icon: '⬡' },
    { id: 'integrations', label: 'Integrations', icon: '⊕' },
    { id: 'billing',      label: 'Billing',      icon: '◊' },
    { id: 'settings',     label: 'Settings',     icon: '⚙' },
  ]},
] as const;

type BadgeKey = 'unreadInbox' | 'pendingDrafts' | 'scheduledPosts';

export function Shell() {
  const { sidebarCollapsed, toggleSidebar, toasts } = useUi();
  const { credits } = useWorkspace();
  const nav = useNavigate();

  const [counts] = useQuery({ query: Q_SHELL_COUNTS, requestPolicy: 'cache-and-network' });
  const c = counts.data?.shellCounts ?? {};
  const badgeFor = (k: BadgeKey | undefined): number | null => {
    if (!k) return null;
    const n = c[k] as number | undefined;
    return n && n > 0 ? n : null;
  };

  // ⌘K palette
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  return (
    <div className="h-screen flex bg-stone-100 font-sans">
      {/* ── SIDEBAR ───────────────────────────────────────────────── */}
      <aside className={`bg-white border-r border-stone-200 flex flex-col transition-all duration-200 ${sidebarCollapsed ? 'w-16' : 'w-60'}`}>
        <div className="px-3 py-3 border-b border-stone-200 flex items-center gap-2">
          <span className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center text-white text-base flex-shrink-0">◈</span>
          {!sidebarCollapsed && <span className="font-semibold flex-1">ContentHub</span>}
          <button onClick={toggleSidebar} className="text-stone-400 hover:text-stone-700" aria-label="Toggle sidebar">
            {sidebarCollapsed ? '»' : '«'}
          </button>
        </div>

        {/* Project switcher — the working context (workspace switching lives in the profile menu) */}
        <div className="px-2 py-2 border-b border-stone-100">
          <ProjectSwitcher collapsed={sidebarCollapsed} />
        </div>

        <nav className="flex-1 px-3 py-2 overflow-y-auto" aria-label="Primary">
          {navSections.map((section) => (
            <div key={section.label} className="mb-3">
              {!sidebarCollapsed && <p className="px-3 text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">{section.label}</p>}
              {section.items.map((item) => {
                const badge = badgeFor((item as any).badgeKey);
                return (
                  <NavLink key={item.id} to={`/app/${item.id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg mb-0.5 ${isActive ? 'bg-violet-50 text-violet-700' : 'text-stone-600 hover:bg-stone-50'}`
                    }>
                    <span className="text-base w-5 text-center flex-shrink-0">{item.icon}</span>
                    {!sidebarCollapsed && (
                      <>
                        <span className="text-sm font-medium flex-1">{item.label}</span>
                        {badge !== null && (
                          <span className="text-[10px] font-semibold min-w-[20px] h-5 px-1.5 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center">{badge}</span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-stone-200 p-2">
          <ProfileMenu collapsed={sidebarCollapsed} />
        </div>
      </aside>

      {/* ── MAIN COLUMN ───────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-stone-200 flex items-center gap-3 px-6 flex-shrink-0">
          {/* ⌘K trigger */}
          <button onClick={() => setPaletteOpen(true)} type="button"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone-200 bg-stone-50 hover:bg-stone-100 text-sm text-stone-500 min-w-[280px]">
            <span className="text-stone-400">⌕</span>
            <span className="flex-1 text-left">Search or jump to…</span>
            <kbd className="text-[10px] text-stone-400 bg-white border border-stone-200 px-1.5 py-0.5 rounded">⌘K</kbd>
          </button>

          <div className="flex-1" />

          {/* Notification bell */}
          <NotificationBell />

          {/* Credit balance */}
          <button onClick={() => nav('/app/billing')} type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm">
            <span>⚡</span>
            <span className="font-semibold text-stone-800">{credits}</span>
            <span className="text-stone-400 text-xs">credits</span>
            <span className="text-stone-400 ml-1">+</span>
          </button>

          {/* Generate quick action */}
          <button onClick={() => nav('/app/create')} type="button"
            className="px-3 py-1.5 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-medium">
            ✨ Generate
          </button>

          {/* + New post primary */}
          <button onClick={() => nav('/app/create')} type="button"
            className="px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium">
            + New post
          </button>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Command palette */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map((t) => (
          <div key={t.id} role="status"
            className={`pointer-events-auto px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              t.kind === 'error' ? 'bg-red-600 text-white'
              : t.kind === 'warning' ? 'bg-amber-500 text-white'
              : 'bg-stone-900 text-white'
            }`}>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
